import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { FaWhatsapp } from "react-icons/fa";

import classNames from "classnames";

import {
  setMechanic,
  setSelectedBranch,
  clearMechanic,
} from "../../redux/mechanic";
import { selectCart, emptyCart } from "../../redux/cart";

import { getMechanicsList } from "../../services/mechanic";

import { dashboardLink } from "../../axios/axios";

import AutoComplete from "../AutoComplete/AutoComplete";
import Select from "../Select/Select";
import ConfirmModal from "../ui/Modal/ConfirmModal";

const MechanicAutoComplete = (props) => {
  const { mechanicId, branchId } = props;
  const selectedMechanic = useSelector((state) => state.mechanic);
  const { createdFromOrderId, voiceOrderId } = useSelector(
    (state) => state.order
  );
  const cart = useSelector(selectCart);
  const dispatch = useDispatch();

  const [displayOptions, setDisplayOptions] = useState(false);
  const [searchText, setSearchText] = useState(
    selectedMechanic
      ? `${selectedMechanic.name} | ${selectedMechanic.primaryPhoneNumber}`
      : ""
  );
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    onOk: () => {},
  });

  function openConfirmModal(onOk) {
    setConfirmModal({ isOpen: true, onOk });
  }

  function closeConfirmModal() {
    setConfirmModal({ isOpen: false, onOk: () => {} });
  }

  function confirmClearCart(onConfirm) {
    if (cart.skus.length > 0 || cart.bundles.length > 0) {
      openConfirmModal(() => {
        onConfirm();
        dispatch(emptyCart());
        closeConfirmModal();
      });
    } else {
      onConfirm();
    }
  }

  function onConfirmClearIput() {
    setSearchText("");
    dispatch(clearMechanic());
  }

  function onConfirmChangeInput(value) {
    setSearchText(value);
    dispatch(clearMechanic());
  }

  function onConfirmSelectMechanicOption(mechanic) {
    dispatch(setMechanic(mechanic));
    setSearchText(`${mechanic.name} | ${mechanic.primaryPhoneNumber}`);
    setDisplayOptions(false);
  }

  const searchInputChangedHandler = (value) => {
    confirmClearCart(onConfirmChangeInput.bind(null, value));
  };

  const optionClickedHandler = (mechanic) => {
    if (selectedMechanic && selectedMechanic.id === mechanic.id) {
      setDisplayOptions(false);
      return;
    }
    confirmClearCart(onConfirmSelectMechanicOption.bind(null, mechanic));
  };

  const branchSelectedHandler = (selectedBranch) => {
    dispatch(setSelectedBranch(selectedBranch));
  };

  const inputClearedHandler = () => {
    confirmClearCart(onConfirmClearIput);
  };

  const hideOptionsHandler = useCallback(() => {
    setDisplayOptions(false);
  }, []);

  const showOptionsHandler = () => {
    setDisplayOptions(true);
  };

  useEffect(() => {
    const getMechanics = async () => {
      setOptionsLoading(true);
      try {
        const response = await getMechanicsList();
        setOptions(response.data.mechanicsList);
        if (mechanicId) {
          const mechanic = response.data.mechanicsList.find(
            (mechanic) => mechanic.id === mechanicId
          );
          if (branchId) {
            mechanic.defaultSelectedBranch = mechanic.branchList.find(
              (branch) => branch.id === branchId
            );
          }
          dispatch(setMechanic(mechanic));
          setSearchText(`${mechanic.name} | ${mechanic.primaryPhoneNumber}`);
        }
      } catch (error) {
        console.log(error);
      }
      setOptionsLoading(false);
    };

    getMechanics();
  }, [mechanicId, branchId, dispatch]);

  let content;

  if (options.length !== 0) {
    content = options
      .reduce((acc, currentOption, index) => {
        if (
          currentOption.name.toLowerCase().indexOf(searchText.toLowerCase()) !==
            -1 ||
          currentOption.primaryPhoneNumber.indexOf(searchText) !== -1 ||
          currentOption.shopName
            .toLowerCase()
            .indexOf(searchText.toLowerCase()) !== -1 ||
          currentOption.areaName
            .toLowerCase()
            .indexOf(searchText.toLowerCase()) !== -1 ||
          currentOption.id.toString().indexOf(searchText) !== -1 ||
          `${currentOption.name} | ${currentOption.primaryPhoneNumber}`.indexOf(
            searchText
          ) !== -1
        ) {
          return [
            ...acc,
            <div key={currentOption.id} className="bg-white">
              <div
                className={classNames(
                  "p-3 hover:bg-gray-200 cursor-pointer flex items-center justify-between",
                  { "bg-gray-200": currentOption.id === selectedMechanic?.id }
                )}
                tabIndex={0}
                onClick={() => optionClickedHandler(currentOption)}
              >
                <div>
                  <span className="block text-sm">
                    {currentOption.primaryPhoneNumber}
                  </span>
                  <span className="block font-bold text-sm">
                    {currentOption.name} - {currentOption.id}
                  </span>
                  <span className="block text-sm">
                    {currentOption.shopName}
                  </span>
                  <span className="block text-sm">
                    {currentOption.areaName}
                  </span>
                </div>

                {currentOption.id === selectedMechanic?.id && (
                  <AiOutlineCheckCircle
                    className="text-end text-green-700"
                    size={20}
                  />
                )}
              </div>

              {index !== options.length - 1 && (
                <hr className="w-10/12 mx-auto" />
              )}
            </div>,
          ];
        } else {
          return [...acc];
        }
      }, [])
      .slice(0, 400);
  }

  return (
    <div className="mt-8 mb-14 px-2 mx-auto flex flex-col items-center sm:flex-row gap-4 flex-wrap justify-center sm:items-end w-full md:w-2/3">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onOk={confirmModal.onOk}
        onCancel={closeConfirmModal}
        message="سيتم حذف محتويات العربة عند تغيير الميكانيكي"
      />
      <div className="w-full md:w-2/5 grow flex items-end gap-2">
        <a
          href={
            selectedMechanic
              ? `https://wa.me/2${selectedMechanic.primaryPhoneNumber}`
              : undefined
          }
          target="_blank"
          rel="noreferrer"
          className={classNames("text-green-600 pb-2", {
            "opacity-40 pointer-events-none": !selectedMechanic,
          })}
        >
          <FaWhatsapp size={28} />
        </a>
        <div className="grow">
          <AutoComplete
            options={content}
            loading={optionsLoading}
            label="اختر الميكانيكي"
            inputPlaceHolder="ابحث بالاسم أو رقم الهاتف"
            onInputChange={searchInputChangedHandler}
            onInputClear={inputClearedHandler}
            hideOptions={hideOptionsHandler}
            showOptions={showOptionsHandler}
            isOptionsVisible={displayOptions}
            inputValue={searchText}
            disabled={createdFromOrderId || voiceOrderId}
          />
        </div>
      </div>

      {selectedMechanic && !optionsLoading && (
        <div className="w-full md:w-1/4 grow">
          <Select
            placeholder="الفرع"
            options={selectedMechanic?.branchList.map((branch) => ({
              branchName: branch.name,
              branchId: branch.id,
            }))}
            optionLabelProp="branchName"
            onSelect={branchSelectedHandler}
            initialValue={selectedMechanic?.selectedBranch?.branchName}
            isDisabled={createdFromOrderId || voiceOrderId}
          />
        </div>
      )}

      {selectedMechanic && !optionsLoading && (
        <a
          className="py-2 text-center border border-dark-blue rounded hover:bg-indigo-50 hover:border-indigo-50 w-full md:w-1/4"
          href={dashboardLink.concat(
            `/mechanic/${selectedMechanic?.id}/details`
          )}
          target="_blank"
          rel="noreferrer"
        >
          تفاصيل الميكانيكي
        </a>
      )}
    </div>
  );
};

export default MechanicAutoComplete;
